import React, { useState } from 'react';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Crown, Building2, Users, Calendar, CheckCircle, X, Star, 
  TrendingUp, MessageCircle, Shield, Sparkles, ArrowRight,
  Phone, Zap, Target, Award, ChevronDown, CreditCard, Loader2
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import authAxios, { API } from '../utils/authAxios';
import { useAuth } from '../contexts/AuthContext';
import MediaCoverage from '../components/MediaCoverage';

// Plan data
const CENTER_PLANS = [
  {
    id: 'gratuito',
    name: 'Gratuito',
    price: 0,
    period: 'siempre',
    description: 'Presencia básica en el directorio',
    position: 'Últimas posiciones',
    color: 'slate',
    features: [
      { text: 'Perfil en directorio', included: true },
      { text: 'Información de contacto', included: true },
      { text: 'Recibir reseñas', included: true },
      { text: 'Posición prioritaria', included: false },
      { text: 'Badge destacado', included: false },
      { text: 'Prioridad en TerapeutAI', included: false },
    ],
    cta: 'Plan Actual',
    ctaDisabled: true
  },
  {
    id: 'destacado',
    name: 'Destacado',
    price: 99000,
    period: 'mes',
    description: 'Mayor visibilidad en el directorio',
    position: 'Top 11-20',
    color: 'blue',
    features: [
      { text: 'Todo del plan Gratuito', included: true },
      { text: 'Badge "Destacado"', included: true },
      { text: 'Posición Top 20', included: true },
      { text: 'Estadísticas básicas', included: true },
      { text: 'Posición Top 10', included: false },
      { text: 'Prioridad máxima en chatbot', included: false },
    ],
    cta: 'Elegir Destacado',
    ctaDisabled: false
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 199000,
    period: 'mes',
    description: 'Máxima visibilidad garantizada',
    position: 'Top 4-10',
    color: 'amber',
    popular: true,
    features: [
      { text: 'Todo del plan Destacado', included: true },
      { text: 'Badge "Premium"', included: true },
      { text: 'Posición Top 10', included: true },
      { text: 'Prioridad en TerapeutAI', included: true },
      { text: 'Estadísticas avanzadas', included: true },
      { text: 'Soporte prioritario', included: true },
    ],
    cta: 'Elegir Premium',
    ctaDisabled: false
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 350000,
    period: 'mes',
    description: 'El mejor posicionamiento posible',
    position: 'Top 1-3',
    color: 'purple',
    features: [
      { text: 'Todo del plan Premium', included: true },
      { text: 'Badge "Elite"', included: true },
      { text: 'Posición Top 3', included: true },
      { text: 'Máxima prioridad en chatbot', included: true },
      { text: 'Destacado en landing page', included: true },
      { text: 'Soporte VIP 24/7', included: true },
    ],
    cta: 'Elegir Elite',
    ctaDisabled: false
  }
];

const PROFESSIONAL_PLANS = [
  {
    id: 'gratuito',
    name: 'Gratuito',
    price: 0,
    period: 'siempre',
    description: 'Presencia básica',
    position: 'Últimas posiciones',
    color: 'slate',
    features: [
      { text: 'Perfil profesional', included: true },
      { text: 'Información de contacto', included: true },
      { text: 'Recibir reseñas', included: true },
      { text: 'Posición prioritaria', included: false },
      { text: 'Badge destacado', included: false },
    ],
    cta: 'Plan Actual',
    ctaDisabled: true
  },
  {
    id: 'destacado',
    name: 'Destacado',
    price: 29000,
    period: 'mes',
    description: 'Mayor visibilidad',
    position: 'Top 11-20',
    color: 'blue',
    features: [
      { text: 'Todo del plan Gratuito', included: true },
      { text: 'Badge "Destacado"', included: true },
      { text: 'Posición Top 20', included: true },
      { text: 'Estadísticas de perfil', included: true },
      { text: 'Prioridad en chatbot', included: false },
    ],
    cta: 'Elegir Destacado',
    ctaDisabled: false
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 49000,
    period: 'mes',
    description: 'Máxima visibilidad',
    position: 'Top 4-10',
    color: 'amber',
    popular: true,
    features: [
      { text: 'Todo del plan Destacado', included: true },
      { text: 'Badge "Premium"', included: true },
      { text: 'Posición Top 10', included: true },
      { text: 'Prioridad en TerapeutAI', included: true },
      { text: 'Soporte prioritario', included: true },
    ],
    cta: 'Elegir Premium',
    ctaDisabled: false
  }
];

const WORKSHOP_PLANS = [
  {
    id: 'gratuito',
    name: 'Gratuito',
    price: 0,
    period: 'siempre',
    description: 'Presencia básica',
    color: 'slate',
    features: [
      { text: 'Perfil del taller', included: true },
      { text: 'Información de contacto', included: true },
      { text: 'Posición prioritaria', included: false },
    ],
    cta: 'Plan Actual',
    ctaDisabled: true
  },
  {
    id: 'destacado',
    name: 'Destacado',
    price: 19000,
    period: 'mes',
    description: 'Mayor visibilidad',
    color: 'emerald',
    popular: true,
    features: [
      { text: 'Todo del plan Gratuito', included: true },
      { text: 'Badge "Destacado"', included: true },
      { text: 'Posición prioritaria', included: true },
      { text: 'Estadísticas de perfil', included: true },
    ],
    cta: 'Elegir Destacado',
    ctaDisabled: false
  }
];

function formatPrice(price) {
  if (price === 0) return 'Gratis';
  return '$' + price.toLocaleString('es-CL');
}

function PlanCard({ plan, type }) {
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  const colorClasses = {
    slate: 'border-slate-700',
    blue: 'border-blue-500/30 bg-blue-500/5',
    amber: 'border-amber-500/30 bg-amber-500/5',
    purple: 'border-purple-500/30 bg-purple-500/5',
    emerald: 'border-emerald-500/30 bg-emerald-500/5'
  };

  const badgeColors = {
    slate: 'text-slate-400',
    blue: 'text-blue-400',
    amber: 'text-amber-400',
    purple: 'text-purple-400',
    emerald: 'text-emerald-400'
  };

  const buttonColors = {
    slate: 'bg-slate-700 hover:bg-slate-600',
    blue: 'bg-blue-500 hover:bg-blue-600',
    amber: 'bg-amber-500 hover:bg-amber-600',
    purple: 'bg-purple-500 hover:bg-purple-600',
    emerald: 'bg-emerald-500 hover:bg-emerald-600'
  };

  const dashboardPath = type === 'center' ? '/dashboard/centro' : type === 'professional' ? '/dashboard/profesional' : '/dashboard/taller';

  async function handleCheckout() {
    // 1) Si el usuario no está logueado, mandarlo a login con redirect post-auth
    if (!isAuthenticated) {
      const target = dashboardPath + '?upgrade=' + plan.id;
      toast.info('Inicia sesión para continuar con tu plan');
      window.location.href = '/login?redirect=' + encodeURIComponent(target);
      return;
    }
    setLoading(true);
    try {
      if (type === 'professional') {
        // Professionals can checkout directly
        var res = await authAxios.post(API + '/professional/premium/checkout', {
          origin_url: window.location.origin,
          plan_type: plan.id
        }, { withCredentials: true });

        if (res.data.url && res.data.token) {
          var form = document.createElement('form');
          form.method = 'POST';
          form.action = res.data.url;
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'token_ws';
          input.value = res.data.token;
          form.appendChild(input);
          document.body.appendChild(form);
          form.submit();
          return;
        } else if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
          return;
        }
      }
      // For centers/workshops, redirect to dashboard where they select the entity
      window.location.href = dashboardPath + '?upgrade=' + plan.id;
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error('Tu sesión expiró. Vuelve a iniciar sesión.');
        window.location.href = '/login?redirect=' + encodeURIComponent(dashboardPath + '?upgrade=' + plan.id);
      } else {
        // Fallback: redirect to dashboard
        window.location.href = dashboardPath + '?upgrade=' + plan.id;
      }
    }
    setLoading(false);
  }

  return (
    <Card className={`relative overflow-hidden ${colorClasses[plan.color]} ${plan.popular ? 'ring-2 ring-amber-500/50' : ''}`} data-testid={`plan-card-${type}-${plan.id}`}>
      {plan.popular && (
        <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
          MÁS POPULAR
        </div>
      )}
      <CardContent className="p-6">
        <div className="mb-4">
          <h3 className={`text-xl font-bold ${badgeColors[plan.color]}`}>{plan.name}</h3>
          <p className="text-slate-400 text-sm">{plan.description}</p>
          {plan.position && (
            <p className="text-xs text-slate-500 mt-1">{plan.position}</p>
          )}
        </div>
        
        <div className="mb-6">
          <span className="text-3xl font-bold text-slate-50">{formatPrice(plan.price)}</span>
          {plan.price > 0 && <span className="text-slate-400"> / {plan.period}</span>}
        </div>
        
        <ul className="space-y-3 mb-6">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm">
              {feature.included ? (
                <CheckCircle className={`w-4 h-4 ${badgeColors[plan.color]}`} />
              ) : (
                <X className="w-4 h-4 text-slate-600" />
              )}
              <span className={feature.included ? 'text-slate-300' : 'text-slate-500'}>
                {feature.text}
              </span>
            </li>
          ))}
        </ul>
        
        {plan.ctaDisabled ? (
          <Button disabled className="w-full bg-slate-700 text-slate-400">
            {plan.cta}
          </Button>
        ) : (
          <Button
            onClick={handleCheckout}
            disabled={loading}
            className={`w-full ${buttonColors[plan.color]}`}
            data-testid={`checkout-plan-${type}-${plan.id}`}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CreditCard className="w-4 h-4 mr-2" />
            )}
            {plan.cta}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ReferralSection() {
  return (
    <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/30">
      <CardContent className="p-8">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Target className="w-10 h-10 text-emerald-400" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-2xl font-bold text-slate-50 mb-2">Plan por Referidos</h3>
            <p className="text-slate-300 mb-4">
              <strong className="text-emerald-400">Sin costo inicial.</strong> Solo pagas el 40% de la primera mensualidad/consulta cuando un paciente ingresa a través de nuestra plataforma.
            </p>
            <div className="flex flex-wrap gap-4 justify-center md:justify-start text-sm">
              <div className="flex items-center gap-2 text-slate-400">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                Sin riesgo inicial
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                Solo pagas por resultados
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                Todos los beneficios Premium
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-600">
              <Link to="/contacto?asunto=plan-referidos">
                <Phone className="w-5 h-5 mr-2" />
                Solicitar Plan
              </Link>
            </Button>
          </div>
        </div>
        
        <div className="mt-6 p-4 bg-slate-900/50 rounded-lg">
          <p className="text-sm text-slate-400 text-center">
            <strong className="text-slate-300">Ejemplo:</strong> Si tu mensualidad es $800.000 CLP, pagarías $320.000 por cada paciente que ingrese. 
            Si tu consulta es $50.000 CLP, pagarías $20.000 por cada paciente nuevo.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function PlanesPage() {
  const [activeTab, setActiveTab] = useState('centros');

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Planes y Precios para Centros y Profesionales | SinAdicciones.org Chile</title>
        <meta name="description" content="Planes de suscripción para centros de rehabilitación y profesionales. Aumenta tu visibilidad y recibe más pacientes. Desde $19.000/mes." />
        <meta name="keywords" content="planes centros rehabilitación, publicidad adicciones, directorio profesionales Chile" />
        <link rel="canonical" href="https://sinadicciones.org/planes" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Planes y Precios | SinAdicciones.org" />
        <meta property="og:description" content="Aumenta tu visibilidad en el directorio de adicciones más grande de Chile. Planes desde $19.000/mes." />
        <meta property="og:url" content="https://sinadicciones.org/planes" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Planes y Precios | SinAdicciones.org" />
        <meta name="twitter:description" content="Aumenta tu visibilidad. Planes desde $19.000/mes." />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
      
      
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/10 mb-6">
              <Crown className="w-8 h-8 text-amber-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 mb-4">
              Planes y Precios
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Aumenta tu visibilidad en el directorio más grande de rehabilitación en Chile. 
              Más de 10,000 familias buscan ayuda cada mes.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">10,000+</p>
              <p className="text-sm text-slate-400">Visitas mensuales</p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">500+</p>
              <p className="text-sm text-slate-400">Leads generados/mes</p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">3x</p>
              <p className="text-sm text-slate-400">Más consultas Premium</p>
            </div>
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">#1</p>
              <p className="text-sm text-slate-400">En Google Chile</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-slate-900 border border-slate-800 rounded-xl p-1">
              <button
                onClick={() => setActiveTab('centros')}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'centros' 
                    ? 'bg-emerald-500 text-white' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Building2 className="w-4 h-4" />
                Centros
              </button>
              <button
                onClick={() => setActiveTab('profesionales')}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'profesionales' 
                    ? 'bg-emerald-500 text-white' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Users className="w-4 h-4" />
                Profesionales
              </button>
              <button
                onClick={() => setActiveTab('talleres')}
                className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
                  activeTab === 'talleres' 
                    ? 'bg-emerald-500 text-white' 
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                <Calendar className="w-4 h-4" />
                Talleres
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          {activeTab === 'centros' && (
            <div className="space-y-8">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {CENTER_PLANS.map(plan => (
                  <PlanCard key={plan.id} plan={plan} type="center" />
                ))}
              </div>
              {false && <ReferralSection />}
            </div>
          )}

          {activeTab === 'profesionales' && (
            <div className="space-y-8">
              <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
                {PROFESSIONAL_PLANS.map(plan => (
                  <PlanCard key={plan.id} plan={plan} type="professional" />
                ))}
              </div>
              {false && <ReferralSection />}
            </div>
          )}

          {activeTab === 'talleres' && (
            <div className="space-y-8">
              <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {WORKSHOP_PLANS.map(plan => (
                  <PlanCard key={plan.id} plan={plan} type="workshop" />
                ))}
              </div>
              {false && <ReferralSection />}
            </div>
          )}

          {/* Benefits Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-50 text-center mb-8">
              ¿Por qué elegir un plan de pago?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-7 h-7 text-amber-400" />
                  </div>
                  <h3 className="font-semibold text-slate-50 mb-2">3x Más Visibilidad</h3>
                  <p className="text-sm text-slate-400">
                    Los perfiles Premium reciben en promedio 3 veces más consultas que los gratuitos.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-slate-50 mb-2">Prioridad en TerapeutAI</h3>
                  <p className="text-sm text-slate-400">
                    Nuestro chatbot recomienda primero los centros y profesionales con plan activo.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-7 h-7 text-blue-400" />
                  </div>
                  <h3 className="font-semibold text-slate-50 mb-2">Badge de Confianza</h3>
                  <p className="text-sm text-slate-400">
                    Los usuarios confían más en perfiles destacados. Mayor tasa de conversión.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Apariciones en Medios */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-50 mb-3">
                Lo que Dicen de <span className="text-blue-400">Nosotros</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Medios nacionales han cubierto el trabajo de SinAdicciones.org. Conoce la historia detrás de la plataforma.
              </p>
            </div>
            <MediaCoverage />
          </div>

          {/* FAQ */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-slate-50 text-center mb-8">
              Preguntas Frecuentes
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-50 mb-2">¿Puedo cancelar en cualquier momento?</h3>
                  <p className="text-sm text-slate-400">Sí, puedes cancelar tu suscripción cuando quieras. Tu plan seguirá activo hasta el fin del período pagado.</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-50 mb-2">¿Qué métodos de pago aceptan?</h3>
                  <p className="text-sm text-slate-400">Aceptamos tarjetas de crédito, débito y prepago a través de Klap (Multicaja / Multivende).</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-slate-50 mb-2">¿Puedo cambiar de plan?</h3>
                  <p className="text-sm text-slate-400">Sí, puedes subir o bajar de plan en cualquier momento. El cambio se aplica en el siguiente período de facturación.</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-16">
            <Card className="bg-gradient-to-r from-slate-900 to-slate-800 border-slate-700">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-slate-50 mb-4">¿Tienes dudas?</h3>
                <p className="text-slate-400 mb-6 max-w-xl mx-auto">
                  Contáctanos y te ayudamos a elegir el plan perfecto para tu centro o práctica profesional.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-600">
                    <a href="https://wa.me/56939014212?text=Hola,%20tengo%20dudas%20sobre%20los%20planes%20de%20suscripción" target="_blank" rel="noopener noreferrer">
                      <Phone className="w-5 h-5 mr-2" />
                      WhatsApp
                    </a>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    <Link to="/contacto">
                      Enviar Mensaje
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
